<script setup lang="ts">
import { onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import StatusChip from '@/components/StatusChip.vue';
import { errorMessage } from '@/utils/format';
import { eightDStep } from '@/utils/eight-d';

/**
 * 8D 报告视图：只列有 8D 步骤的问题，每行一条 D0–D8 进度。
 *
 * 客户审核会问「开着几个 8D、各走到哪一步」——163 条的混合问题清单
 * 答不了这个问题（有 8D 的只占 4%），所以单独一个视图；
 * 但数据仍挂在问题下，不另建台账，避免双录走岔。
 */
interface BoardStep { stepCode: string; status: string | null; title: string | null }
interface BoardIssue {
  id: number;
  code: string | null;
  title: string | null;
  status: string | null;
  ownerName: string | null;
  projectNo: string | null;
  projectName: string | null;
  steps: BoardStep[];
}

/**
 * open 带上 step：从进度条里点某个空缺的 D 步进来，
 * 抽屉直接展开 8D 新建表单并选好那一步——原来要打开问题再滚到抽屉底部才找得到。
 */
const emit = defineEmits<{ open: [id: number, step?: string] }>();

const items = ref<BoardIssue[]>([]);
const loading = ref(false);
const error = ref('');

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.issue8dBoard();
    items.value = payload.items as unknown as BoardIssue[];
  } catch (caught) {
    items.value = [];
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

onMounted(load);
defineExpose({ reload: load });

const D_STEPS = ['d0', 'd1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8'];
const DONE = new Set(['completed', 'closed', 'approved']);

/**
 * 每个 D 步的汇总状态：done = 该步全部完成；active = 有记录但没走完；
 * none = 还没做到这步。作废的记录不算。
 */
function stepState(issue: BoardIssue, step: string): 'done' | 'active' | 'none' {
  const relevant = issue.steps.filter((item) => {
    const code = item.stepCode.replace(/^review:/, '');
    return code.split(':')[0] === step && item.status !== 'cancelled';
  });
  if (!relevant.length) return 'none';
  return relevant.every((item) => DONE.has(String(item.status ?? ''))) ? 'done' : 'active';
}

const STATE_LABEL = { done: '已完成', active: '进行中', none: '未开始' } as const;

/* 状态列显式传中文 label（1027 UI 审计），口径同问题清单的 statusLabel */
const STATUS_LABEL: Record<string, string> = {
  open: '待处理',
  pending: '待处理',
  in_progress: '处理中',
  blocked: '受阻',
  closed: '已关闭',
  completed: '已完成',
  approved: '已通过',
  cancelled: '已取消',
};

function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

function stepTitle(issue: BoardIssue, step: string): string {
  const guide = eightDStep(step.toUpperCase());
  const state = STATE_LABEL[stepState(issue, step)];
  const name = guide ? ` ${guide.name}` : '';
  const action = stepState(issue, step) === 'none' ? '，点一下直接补录' : '';
  return `${step.toUpperCase()}${name}：${state}${action}`;
}

/** 当前卡在哪一步：第一个非 done 且有内容的 D 步 */
function currentStep(issue: BoardIssue): string {
  for (const step of D_STEPS) {
    if (stepState(issue, step) === 'active') return step.toUpperCase();
  }
  return '—';
}
</script>

<template>
  <div class="eightd-board">
    <p v-if="loading" class="board-hint">正在读取 8D 报告…</p>
    <p v-else-if="error" class="board-hint board-error">读取失败：{{ error }}</p>
    <p v-else-if="!items.length" class="board-hint">
      还没有任何问题带 8D 步骤。到「问题清单」打开一条问题，抽屉里的「8D 步骤（D0–D8）」就能录第一步——
      每一步都写清了要填什么。
    </p>

    <div v-else class="data-table-wrap">
      <table class="data-table data-table--rows-link">
        <thead>
          <tr>
            <th>问题编号</th><th class="col-name">问题标题</th><th>项目</th><th>责任人</th>
            <th>D0–D8 进度</th><th>当前步</th><th class="col-status">状态</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="issue in items" :key="issue.id" @click="emit('open', issue.id)">
            <td>{{ issue.code || issue.id }}</td>
            <td class="board-title col-name">{{ issue.title }}</td>
            <td>{{ issue.projectNo || '—' }}</td>
            <td>{{ issue.ownerName || '—' }}</td>
            <td>
              <span class="d-track">
                <button
                  v-for="step in D_STEPS"
                  :key="step"
                  type="button"
                  class="d-chip"
                  :class="`d-${stepState(issue, step)}`"
                  :title="stepTitle(issue, step)"
                  @click.stop="emit('open', issue.id, step.toUpperCase())"
                >{{ step.toUpperCase() }}</button>
              </span>
            </td>
            <td><b class="d-current">{{ currentStep(issue) }}</b></td>
            <td class="col-status"><StatusChip :value="issue.status || 'open'" :label="statusLabel(issue.status || 'open')" /></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.board-hint { margin: 14px 0 0; color: var(--muted); font-size: 13px; line-height: 1.8; }
.board-error { color: var(--risk); }
.board-title { max-width: 320px; }
.col-name { min-width: 160px; }
.col-status { white-space: nowrap; }
.d-track { display: inline-flex; gap: 3px; }
.d-chip {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 26px; padding: 2px 0; border: 0; border-radius: 4px;
  font-family: inherit; font-style: normal; font-size: 10px; font-weight: 800;
  letter-spacing: .02em; cursor: pointer;
}
.d-chip:hover { outline: 1px solid var(--blue); }
.d-none { background: rgba(120, 130, 140, .1); color: var(--muted); }
.d-active { background: rgba(0, 117, 174, .14); color: var(--blue); }
.d-done { background: rgba(31, 154, 92, .16); color: var(--ok, #1f9a5c); }
.d-current { color: var(--blue); }
</style>
